import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { GridApi } from './api/gridApi'
import type { GridAdditionalFilterValue, GridColumnFilterValue, GridConfig, GridRow, GridSortItem } from './types'

type Pagination = { page: number; pageSize: number }

type State = {
	rows: GridRow[]
	total: number
	loading: boolean
	pagination: Pagination
	sort: GridSortItem[]
	columnFilters: GridColumnFilterValue[]
	additionalFilters: GridAdditionalFilterValue[]
	selection: ReadonlyArray<string | number>
}

type Actions = {
	setPagination: (p: Pagination) => void
	setSort: (s: GridSortItem[]) => void
	setColumnFilters: (f: GridColumnFilterValue[]) => void
	setAdditionalFilters: (f: GridAdditionalFilterValue[]) => void
	setSelection: (s: ReadonlyArray<string | number>) => void
	refresh: () => void
}

export const useGridData = (config: GridConfig, api: GridApi): State & Actions => {
	const [rows, setRows] = useState<GridRow[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(true)
	const [pagination, setPagination] = useState<Pagination>({
		page: 0,
		pageSize: config.pagination.defaultPageSize,
	})
	const [sort, setSort] = useState<GridSortItem[]>(config.defaultSort)
	const [columnFilters, setColumnFilters] = useState<GridColumnFilterValue[]>([])
	const [additionalFilters, setAdditionalFilters] = useState<GridAdditionalFilterValue[]>([])
	const [selection, setSelection] = useState<ReadonlyArray<string | number>>([])
	const [refreshTick, setRefreshTick] = useState(0)

	// Stable snapshot of every value the fetch payload reads. We re-run the effect
	// only when this *content* changes, not when array/object identities shuffle —
	// MUI's controlled callbacks routinely hand us new array refs with identical
	// contents and that used to fire a fresh fetch every render.
	const payloadKey = useMemo(
		() => JSON.stringify({ pagination, sort, columnFilters, additionalFilters, refreshTick }),
		[pagination, sort, columnFilters, additionalFilters, refreshTick],
	)

	const paginationMode = config.pagination.mode

	// We need the latest values inside the timeout callback without making the
	// effect itself re-run on every state churn — keep them in a ref.
	const latestRef = useRef({ pagination, sort, columnFilters, additionalFilters, paginationMode })
	latestRef.current = { pagination, sort, columnFilters, additionalFilters, paginationMode }

	useEffect(() => {
		let cancelled = false
		setLoading(true)

		const handle = setTimeout(() => {
			const snap = latestRef.current
			api
				.fetch({
					offset: snap.pagination.page * snap.pagination.pageSize,
					limit: snap.pagination.pageSize,
					sort: snap.sort,
					columnFilters: snap.columnFilters,
					additionalFilters: snap.additionalFilters,
				})
				.then((res) => {
					if (cancelled) {
						return
					}
					if (snap.paginationMode === 'lazy' && snap.pagination.page > 0) {
						setRows((existing) => [...existing, ...res.rows])
					} else {
						setRows(res.rows)
					}
					setTotal(res.total)
					setLoading(false)
				})
				.catch(() => {
					if (!cancelled) {
						setLoading(false)
					}
				})
		}, 250)

		return () => {
			cancelled = true
			clearTimeout(handle)
		}
	}, [api, payloadKey])

	const refresh = useCallback(() => setRefreshTick((t) => t + 1), [])

	useEffect(() => {
		const handler = (event: Event): void => {
			const detail = (event as CustomEvent<{ gridId?: string }>).detail
			if (detail?.gridId === config.id) {
				refresh()
			}
		}
		document.addEventListener('reactGrid:refresh', handler)
		return () => document.removeEventListener('reactGrid:refresh', handler)
	}, [config.id, refresh])

	return {
		rows,
		total,
		loading,
		pagination,
		sort,
		columnFilters,
		additionalFilters,
		selection,
		setPagination,
		setSort,
		setColumnFilters,
		setAdditionalFilters,
		setSelection,
		refresh,
	}
}
